import React, { useState, useEffect } from 'react';
import { permissionService } from '../../services/permissionService';
import { HmgPermissionFormData } from '../../types';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';

interface PermissionFormModalProps {
  permission: any | null;
  onClose: () => void;
  onSuccess: () => void;
}

const PermissionFormModal: React.FC<PermissionFormModalProps> = ({
  permission,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<HmgPermissionFormData>({
    module: '',
    action: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (permission) {
      setFormData({
        module: permission.module,
        action: permission.action,
        description: permission.description || '',
      });
    }
  }, [permission]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      setLoading(true);
      if (permission) {
        await permissionService.update(permission.id, formData);
      } else {
        await permissionService.create(formData);
      }
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao salvar permissão');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={permission ? 'Editar Permissão' : 'Nova Permissão'}
    >
      <form onSubmit={handleSubmit} className="p-6">
        {error && (
          <Alert type="error" className="mb-4" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <div className="space-y-4">
          <div>
            <label htmlFor="module" className="block text-sm font-medium text-gray-700 mb-1">
              Módulo *
            </label>
            <Input
              id="module"
              type="text"
              value={formData.module}
              onChange={(e) => setFormData({ ...formData, module: e.target.value })}
              required
              placeholder="Ex: parceiros, associados, beneficios..."
              disabled={!!permission}
            />
            {permission && (
              <p className="mt-1 text-xs text-gray-500">O módulo não pode ser alterado</p>
            )}
          </div>

          <div>
            <label htmlFor="action" className="block text-sm font-medium text-gray-700 mb-1">
              Ação *
            </label>
            <Input
              id="action"
              type="text"
              value={formData.action}
              onChange={(e) => setFormData({ ...formData, action: e.target.value })}
              required
              placeholder="Ex: create, read, update, delete, list..."
              disabled={!!permission}
            />
            {permission && (
              <p className="mt-1 text-xs text-gray-500">A ação não pode ser alterada</p>
            )}
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Descrição
            </label>
            <Input
              id="description"
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descreva o que esta permissão permite fazer..."
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Salvando...' : permission ? 'Atualizar' : 'Criar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default PermissionFormModal;
